import { createSelector } from "reselect";
import { getSearchParameters } from "./AppPath";

import { Process, ProcessReferenceModel } from "./Process";
import { ProcessAggregateCount } from "./ProcessAggregateCount";
import { getAggregateCountsForCurrentAgentGroup } from "./ProcessCountSelectors";
import { ProcessColumnIdentifier } from "./ProcessList";
import { ProcessWithStatus } from "./ProcessWithStatus";
import { State } from "./State";

const getProcesses = (state: Partial<State>) => state.processes;
const getUsedInProcesses = (state: Partial<State>) => state.usedInProcessesMap;
const getUsedProcesses = (state: Partial<State>) => state.usedProcessesMap;
const getLookup = (state: Partial<State>) => state.referencedProcessMap;
const getLogLevelUsage = (state: Partial<State>) => state.logLevelUsage;
const getLogRetentionUsage = (state: Partial<State>) => state.logRetentionUsage;
const getAgentGroups = (state: Partial<State>) => state.agentGroups;
const getCurrentAgentGroup = (state: Partial<State>) => state.currentAgentGroupName;


export type ProcessWithProcessReferences = Process & ProcessUsageTypes;
export const getProcessesWithProcessReferences = createSelector(
    [getProcesses, getLookup, getUsedInProcesses, getUsedProcesses, getLogLevelUsage, getLogRetentionUsage, getAgentGroups, getCurrentAgentGroup],
    (allProcesses, allProcessLookup, usedInProcesses, usedProcesses, logLevelUsage, logRetentionUsage, agentGroups, currentAgentGroupName) => {
        if (!allProcesses || !allProcessLookup || !agentGroups || !currentAgentGroupName) {
            return [];
        }

        return allProcesses.map((element): ProcessWithProcessReferences => {
            let usedByProcessesList: ProcessReferenceModel[][] = [];
            let usedProcessesList: ProcessReferenceModel[][] = [];
            let processLogLevel = "";
            let processLogRetention = "";
            if (usedInProcesses && usedInProcesses[element.uniqueIdentifier]) {
                const currentSubprocess = usedInProcesses[element.uniqueIdentifier];

                usedByProcessesList = Object.keys(currentSubprocess).filter((m) => allProcessLookup[m] != null)
                    .map((processGuid) => allProcessLookup[processGuid].map((x) => {
                        return new ProcessReferenceModel(x, currentSubprocess[processGuid], agentGroups, currentAgentGroupName);
                    }));
            }
            if (usedProcesses && usedProcesses[element.uniqueIdentifier]) {
                const currentProcess = usedProcesses[element.uniqueIdentifier];

                usedProcessesList = Object.keys(currentProcess)
                    .map((processGuid) => allProcessLookup[processGuid] ? allProcessLookup[processGuid].map((x) => {
                        return new ProcessReferenceModel(x, currentProcess[processGuid], agentGroups, currentAgentGroupName);
                    }) : [{ processGuid: processGuid }] as ProcessReferenceModel[]);   // Return a empty IUsedProcess if the subprocess was not found in the agent group allProcessLookup
            }

            if (logLevelUsage && logLevelUsage[element.uniqueIdentifier]) {
                processLogLevel = logLevelUsage[element.uniqueIdentifier];
            }

            if (logRetentionUsage && logRetentionUsage[element.uniqueIdentifier]) {
                processLogRetention = logRetentionUsage[element.uniqueIdentifier].toString();
            }

            return Object.assign(element, {
                usedByProcessesList: usedByProcessesList,
                usedProcessesList: usedProcessesList,
                logLevel: processLogLevel,
                logRetention: processLogRetention
            }) as ProcessWithProcessReferences;
        });
    });

const isSortedByExecutionColumn = (sortColumn: keyof (ProcessAggregateCount)) => {
    switch (sortColumn) {
        case "erroredNotAcknowledged":
        case "executionsPerDay":
        case "failed":
        case "latestExecutionStartTime":
        case "latestExecutionState":
        case "latestFailed":
        case "latestRunning":
        case "latestSuccessful":
        case "running":
        case "successful":
        case "total":
            return true;
        default: return false;
    }
};

function getPropertyValueForSort(process: Process | any, prop: string): string {
    return (process[prop] != null ? process[prop].toString() : "");
}
const numericAccentSensitiveCollation = new Intl.Collator(undefined, { numeric: true, sensitivity: "accent" });
/**
 * https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
 */
function sortByProp(prop: keyof ProcessWithStatus): (a: any, b: any) => number {

    return (a: any, b: any) =>
        numericAccentSensitiveCollation.compare(
            getPropertyValueForSort(a, prop),
            getPropertyValueForSort(b, prop));
}


export const getProcessesWithStatus = createSelector(
    [getProcessesWithProcessReferences, getAggregateCountsForCurrentAgentGroup],
    (processes, processCounts) => {
        let processesWithStatus = processes as ProcessWithStatus[];
        if (processCounts) {
            processesWithStatus = processes.map((p) => Object.assign({}, p, processCounts[p.uniqueIdentifier]));
        }

        const sortBy = getSearchParameters().sortBy as keyof ProcessWithStatus;

        if (sortBy && isSortedByExecutionColumn(sortBy as keyof (ProcessAggregateCount))) {

            if (sortBy == ProcessColumnIdentifier.executionsPerDay) {
                return processesWithStatus.slice().sort((x, c) =>
                    c.executionsPerDay == null || x.executionsPerDay == null ? 0 // sometimes there are no counts for the past X days, put those last
                        // Sort by last day executions
                        : c.executionsPerDay[c.executionsPerDay.length - 1] - x.executionsPerDay[x.executionsPerDay.length - 1]);
            } else {
                return processesWithStatus.slice().sort(sortByProp(sortBy)).reverse();
            }

        }
        return processesWithStatus;
    });

interface ProcessUsageTypes {
    usedByProcessesList: ProcessReferenceModel[][];
    usedProcessesList: ProcessReferenceModel[][];
    logLevel: string;
    logRetention: string;
}